import type {
	FolderInfo,
	FolderPopulated,
	FolderTitle,
	FolderParent,
	ApiResponse,
} from "@vita-learning/types";

import { revalidate } from "~/shared/utils";

import { type HttpConfig, resHttpClient as httpClient } from "../../config";
import { assertCorrectUrlEndpoint } from "../../utils";

import { decodeFolder } from "./helpers";

export const createFolder = async (
	folder: FolderInfo
): Promise<FolderPopulated> => {
	const resp = await httpClient.post<ApiResponse<FolderPopulated>>(
		"/folders",
		folder
	);

	if (!resp.success) throw resp.error;

	await revalidate("folders");

	return decodeFolder(resp.data);
};

export const getFolder = async (id: string, config?: HttpConfig) => {
	const resp = await httpClient.get<ApiResponse<FolderPopulated>>(
		`/folders/${id}`,
		config
	);

	if (resp.debug) console[resp.debug.level](resp.debug);

	if (!resp.success) throw resp.error;

	return decodeFolder(resp.data);
};

export const fetchFolders = async (
	url: string,
	config?: HttpConfig
): Promise<FolderPopulated[]> => {
	assertCorrectUrlEndpoint(url, "/folders");

	const resp = await httpClient.get<ApiResponse<FolderPopulated[] | null>>(
		url,
		config
	);

	if (resp.debug) console[resp.debug.level](resp.debug);

	if (!resp.success) throw resp.error;

	return resp.data?.map(decodeFolder) || [];
};

export const updateFolder = async (
	folder: FolderInfo
): Promise<FolderPopulated> => {
	if (!folder.id) throw new Error("Folder ID is required");

	const resp = await httpClient.put<ApiResponse<FolderPopulated>>(
		`/folders/${folder.id}`,
		folder
	);

	if (resp.debug) console[resp.debug.level](resp.debug);

	if (!resp.success) throw resp.error;

	await revalidate("folders");

	return decodeFolder(resp.data);
};

export const modifyFolderParent = async (
	noteFolder: FolderParent
): Promise<FolderPopulated> => {
	const resp = await httpClient.patch<ApiResponse<FolderPopulated>>(
		`/folders/${noteFolder.id}/folderId`,
		noteFolder
	);

	if (resp.debug) console[resp.debug.level](resp.debug);

	if (!resp.success) throw resp.error;

	await revalidate("folders");

	return decodeFolder(resp.data);
};
export const modifyFolderTitle = async (
	noteTitle: FolderTitle
): Promise<FolderPopulated> => {
	const resp = await httpClient.patch<ApiResponse<FolderPopulated>>(
		`/folders/${noteTitle.id}/title`,
		noteTitle
	);

	if (resp.debug) console[resp.debug.level](resp.debug);

	if (!resp.success) throw resp.error;

	await revalidate("folders");

	return decodeFolder(resp.data);
};

export const deleteFolder = async (id: string): Promise<void> => {
	const resp = await httpClient.delete<ApiResponse<void>>(`/folders/${id}`);

	if (resp.debug) console[resp.debug.level](resp.debug);

	if (!resp.success) throw resp.error;

	await revalidate("folders");

	return resp.data;
};
